import { useQuery } from '@apollo/client/react';
import { ADVISORS_MY_FOUNDERS_PAGE_SIZE, ADVISORS_ORDER_BY_LAST_ACTIVITY } from '@src/components/sco/Advisors/constants';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { EngagementSearchList, EngagementSearchListItem } from '@src/components/sco/Advisors/shared/EngagementSearchList';
import { founderDisplayName, founderSubtitle, isActiveFounderEngagement, myFoundersHref } from '@src/components/sco/Advisors/utils/mapFounderEngagements';
import { ADVISOR_ENGAGEMENT_STATUS_ACTIVE } from '@src/constants/advisor-engagement-statuses';
import { ADVISOR_FOUNDER_ENGAGEMENTS } from '@src/gql/queries/AdvisorFounderEngagements';
import { ScoAdvisorEngagement } from '@src/graphql-types';
import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useMemo, useRef, useState } from 'react';
type FounderEngagementsResult = {
  advisorEngagements?: {
    items?: Array<ScoAdvisorEngagement | null> | null;
    pagination?: {
      itemCount?: number | null;
      pageCount?: number | null;
      page?: number | null;
      pageSize?: number | null;
      hasNextPage?: boolean | null;
    } | null;
  } | null;
};
export const MyFoundersListSection = () => {
  const paths = useAdvisorsPaths();
  useAdvisorsToolbar({
    breadcrumbs: advisorsBreadcrumbsWithTrail({
      title: 'My founders',
      href: `${paths.base}/my-founders`
    }, {
      title: 'View all',
      href: paths.myFoundersAll
    })
  });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search.trim(), 300);

  // When the debounced term changes, always show page 1 (avoids empty pages for narrow filters).
  useEffect(() => {
    setPage(current => current === 1 ? current : 1);
  }, [debouncedSearch]);
  const query = useQuery<FounderEngagementsResult>(ADVISOR_FOUNDER_ENGAGEMENTS, {
    variables: {
      statusKeys: [ADVISOR_ENGAGEMENT_STATUS_ACTIVE],
      page,
      pageSize: ADVISORS_MY_FOUNDERS_PAGE_SIZE,
      search: debouncedSearch || undefined,
      orderBy: ADVISORS_ORDER_BY_LAST_ACTIVITY
    },
    // Avoid serving a stale empty result for a search term from the cache.
    fetchPolicy: debouncedSearch ? 'network-only' : 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });
  const pagination = query.data?.advisorEngagements?.pagination;
  const itemCount = pagination?.itemCount ?? 0;
  const pageCount = Math.max(1, pagination?.pageCount ?? 1);
  const items = useMemo((): EngagementSearchListItem[] => {
    const engagements = (query.data?.advisorEngagements?.items || []).filter(isActiveFounderEngagement) as ScoAdvisorEngagement[];
    return engagements.map(engagement => {
      const founderUserId = engagement.founderUserId || engagement.founderUser?.userId;
      const name = founderDisplayName(engagement);
      const subtitle = founderSubtitle(engagement);
      const companyName = engagement.company?.name || engagement.founderUser?.company?.name;
      return {
        key: engagement.advisorEngagementId || engagement.id || founderUserId || name,
        name,
        href: myFoundersHref(founderUserId, paths.base),
        userId: founderUserId,
        subtitle,
        searchText: [name, subtitle, companyName, engagement.founderUser?.displayName].filter(Boolean).join(' ')
      };
    });
  }, [query.data?.advisorEngagements?.items, paths.base]);
  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  // Only blank the page on the very first fetch. Search/page refetches must keep
  // EngagementSearchList mounted so the input retains focus (network-only can clear
  // `data` between variable changes, so we key off a sticky "has loaded" flag).
  const hasLoadedOnceRef = useRef(false);
  if (query.data || query.error) {
    hasLoadedOnceRef.current = true;
  }
  const initialLoad = query.loading && !hasLoadedOnceRef.current;
  return <AdvisorsQueryState error={query.error} loading={initialLoad}>
      <EngagementSearchList emptyMessage={'No active founder relationships yet.'} itemCount={itemCount} items={items} loading={query.loading} noMatchMessage={'No founders match your search.'} page={page} pageCount={pageCount} search={search} searchPlaceholder={'Search founders by name...'} serverSide={true} subtitle={'Your active founder relationships. Search by name, then open a conversation.'} title={'My Founders'} onPageChange={setPage} onSearchChange={handleSearchChange} />
    </AdvisorsQueryState>;
};